import { useCallback, useEffect, useState } from 'react';
import { Form, Spin, Typography, Input, Checkbox, Alert, InputNumber, message } from 'antd';
import { Banner } from 'components/ui/Banner';
import Card from 'components/Card/Card';
import PageMeta from 'components/PageMeta/PageMeta';
import { FAMILY_APPLICATION_STEPS, FAMILY_BRANCH_MAP } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import RadioCards from 'components/RadioCards/RadioCards';
import Select from 'components/Select/Select';
import { familyService } from 'services';
import { DatePicker } from 'components/ui/DatePicker';
import { AsyncSelect } from 'components/Select';
import { getOtherAdultFirstName, getOtherAdultFullName, getApplicantFullName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { RevealingLink } from 'components/ui/RevealingLink/RevealingLink';
import ExternalLink from 'components/ExternalLink/ExternalLink';
import { getSpouseTitle } from './constants';

const { Text } = Typography;

export function FamilyAppSpouseSelfEmployment({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  const isCompletedBySponsor = application?.completedBySponsor === true;

  // Form state
  const [form] = Form.useForm();
  const businessCountFormValue = Form.useWatch('businessCount', form);
  const taxDocsAvailableFormValue = Form.useWatch('taxDocsAvailable', form);
  const certificationHoursFormValue = Form.useWatch('certificationHours', form);

  const canShowSelfEmploymentWarning = [
    FAMILY_BRANCH_MAP.marineCorps.id,
    FAMILY_BRANCH_MAP.airForce.id,
    FAMILY_BRANCH_MAP.spaceForce.id,
  ].includes(application?.applicant?.militaryDetails?.branch?.id);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const updatedSpouse = await familyService.addUpdateAdult(
          household?.id,
          application?.additionalParents?.[0]?.id ?? null,
          {
            ...application?.additionalParents?.[0],
            selfEmploymentDetails: {
              ...application?.additionalParents?.[0]?.selfEmploymentDetails,
              type: values.type,
              taxDocsAvailable: values.taxDocsAvailable,
              hoursPerWeek: values.hoursPerWeek,
              businessCount: values.businessCount?.id,
              // Self certification
              certificationAccepted: values.certificationAccepted,
              certificationSigned: values.certificationSigned,
              annualIncome: values.annualIncome,
              businessName: values.businessName,
              businessDescription: values.businessDescription,
              certificationHours: values.certificationHours,
              businessState: values.businessState,
              startDate: values.startDate,
              certificationSigner: values.certificationSigner,
            },
          },
        );

        const _newApplication = await familyService.saveApplication({
          ...application,
          additionalParents: [updatedSpouse],
        });

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic?.noticeError?.(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.additionalParents?.[0]?.selfEmploymentDetails) {
      const selfEmploymentDetails = application.additionalParents[0].selfEmploymentDetails;

      form.setFieldsValue({
        type: selfEmploymentDetails.type,
        taxDocsAvailable: selfEmploymentDetails.taxDocsAvailable,
        hoursPerWeek: selfEmploymentDetails.hoursPerWeek,
        businessCount: { id: selfEmploymentDetails.businessCount },
        // Self certification
        certificationAccepted: selfEmploymentDetails.certificationAccepted,
        certificationSigned: selfEmploymentDetails.certificationSigned,
        annualIncome: selfEmploymentDetails.annualIncome,
        businessName: selfEmploymentDetails.businessName,
        businessDescription: selfEmploymentDetails.businessDescription,
        certificationHours: selfEmploymentDetails.certificationHours,
        businessState: selfEmploymentDetails.businessState,
        startDate: selfEmploymentDetails.startDate,
        certificationSigner: selfEmploymentDetails.certificationSigner,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Spouse Self-Employment Information" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            question={
              isCompletedBySponsor
                ? `Tell us about ${getOtherAdultFirstName(application?.additionalParents?.[0])}'s self-employment`
                : 'Tell us about your self-employment'
            }
            className="my-12"
          />

          <Form.Item
            name="type"
            label="What type?"
            rules={[{ required: true, message: 'Please specify type of self-employment' }]}
            className="mb-8"
          >
            <RadioCards
              options={[
                {
                  value: 'Contractor',
                  label: 'Independent contractor (1099 or W-2)',
                },
                {
                  value: 'BusinessOwner',
                  label: 'Business owner',
                },
                {
                  value: 'ContractorAndBusinessOwner',
                  label: 'Both',
                },
              ]}
              direction="vertical"
            />
          </Form.Item>

          <Form.Item
            name="taxDocsAvailable"
            label={`Do ${isCompletedBySponsor ? 'they' : 'you'} have tax documents?`}
            rules={[
              {
                required: true,
                message: `Please specify whether ${isCompletedBySponsor ? 'they' : 'you'} have tax documents`,
              },
            ]}
            className="mb-8"
          >
            <RadioCards
              options={[
                {
                  value: true,
                  label: 'Yes',
                },
                {
                  value: false,
                  label: 'No',
                },
              ]}
              direction="vertical"
            />
          </Form.Item>

          {taxDocsAvailableFormValue === false && (
            <Card
              className="mb-8"
              title="Certification of Self-Employment"
              subtitle="Adults that are self-employed must work between 16-40 hours per week to be eligible. This form can be
                used if tax documents are unavailable"
              color="primary"
            >
              {canShowSelfEmploymentWarning && (
                <Alert
                  message="IMPORTANT"
                  description="If a Schedule C has not been filed yet, you must submit the business license and this self-employment statement signed by the military sponsor or second parent."
                  type="warning"
                  showIcon
                  className="my-4"
                />
              )}

              <Alert
                message="Self-employed person"
                description={
                  <div>
                    A self-employed person is an individual who earns a living by working for themselves, not as an
                    owner of a corporation. Earnings must, at a minimum, exceed minimum hourly wages for the state the
                    business is operated in.&nbsp;
                    <MinimumWageInfo />
                  </div>
                }
                showIcon={false}
              />

              <div className="mt-6">
                <Form.Item
                  name="businessName"
                  label="Name of business"
                  rules={[{ required: true, message: 'Please enter the name of the business' }]}
                >
                  <Input placeholder="Enter name of business" />
                </Form.Item>

                <Form.Item
                  name="businessDescription"
                  label="Description of business"
                  rules={[{ required: true, message: 'Please enter the description of the business' }]}
                >
                  <Input placeholder="Enter description of business" />
                </Form.Item>

                <Form.Item
                  name="businessState"
                  label="State where business is operated"
                  rules={[{ required: true, message: 'Please select a state' }]}
                >
                  <AsyncSelect
                    placeholder="Select..."
                    ariaLabel="State"
                    optionsApiUrl="/countries/states/US"
                    getOptionLabel={(opt) => opt.name}
                    getOptionValue={(opt) => opt.code}
                    isOptionSelected={(option, [value]) => option?.code === value}
                    onChangeFormatter={(v) => v?.code}
                  />
                </Form.Item>

                <Form.Item
                  name="startDate"
                  label="Start date of business"
                  rules={[{ required: true, message: 'Please enter the start date' }]}
                >
                  <DatePicker />
                </Form.Item>

                <div className="flex flex-col gap-6 md:flex-row md:gap-6">
                  <div className="md:w-1/2">
                    <Form.Item
                      name="certificationHours"
                      label="Average weekly work hours"
                      rules={[{ required: true, message: 'Please enter the average weekly work hours' }]}
                    >
                      <InputNumber size="large" addonAfter="Hours" min={0} controls={false} className="w-full" />
                    </Form.Item>
                  </div>

                  <div className="md:w-1/2">
                    <Form.Item
                      name="annualIncome"
                      label="Estimated gross annual income"
                      rules={[{ required: true, message: 'Please enter the estimated gross annual income' }]}
                    >
                      <InputNumber
                        size="large"
                        addonBefore="$"
                        min={0}
                        controls={false}
                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value.replace(/,/g, '')}
                        className="w-full"
                      />
                    </Form.Item>
                  </div>
                </div>

                <Text>
                  <div className="pb-6">
                    I certify that, the <strong>{getSpouseTitle(application)}</strong> of{' '}
                    <strong>{getApplicantFullName(application?.applicant)}</strong>:{' '}
                    <strong>{getOtherAdultFullName(application?.additionalParents?.[0])}</strong> works{' '}
                    <strong>{certificationHoursFormValue ?? '-'}</strong> hours per week.
                  </div>
                </Text>

                <Alert
                  className="mb-6"
                  description={
                    <div>
                      You must <span className="font-bold">notify</span> Child Care Aware® of America immediately on the
                      day that any employment details change, including pay rate, hours, or if employment ends. Failure
                      to report changes immediately may result in over-payment that must be repaid. Misrepresentation or
                      falsifying this information will be reported to your branch of service, it may result in
                      prosecution under applicable State and Federal laws See 18 U.S.C. 1001, and may subject the
                      individual to removal from the program and require the Sponsor to be responsible for repaying any
                      fee assistance benefits that they were not entitled to receive based upon the Fee Assistance
                      guidelines.
                    </div>
                  }
                  showIcon={false}
                />

                <Form.Item
                  name="certificationAccepted"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value ? Promise.resolve() : Promise.reject(new Error('You must accept the certification')),
                    },
                  ]}
                >
                  <Checkbox>
                    I attest that I must notify Child Care Aware® of America in writing to report a change of possible
                    eligibility status should {getOtherAdultFullName(application?.additionalParents?.[0], true)}{' '}
                    employment change. I understand that my fee assistance may be discontinued, making me financially
                    responsible for all child care costs if {getOtherAdultFullName(application?.additionalParents?.[0])}{' '}
                    does not work as required as required by the by the Fee Assistance Program guidelines.{' '}
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="certificationSigned"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value
                          ? Promise.resolve()
                          : Promise.reject(new Error('You must accept the electronic signature')),
                    },
                  ]}
                >
                  <Checkbox>
                    By checking this box, I electronically sign this Certification of Self-Employment form and attest
                    that the above information is true and accurate. I understand this digital signature has the same
                    legal effect and enforce-ability as a handwritten signature.
                  </Checkbox>
                </Form.Item>

                <Form.Item
                  name="certificationSigner"
                  label="Enter your full name to certify the information above"
                  rules={[{ required: true, message: 'Please enter your full name' }]}
                >
                  <Input />
                </Form.Item>
              </div>
            </Card>
          )}

          <Form.Item
            name="hoursPerWeek"
            label="Hours per week"
            rules={[
              { required: true, message: 'Please enter the hours per week' },
              {
                validator: (_, value) =>
                  value > 100
                    ? Promise.reject(new Error('Please verify hours (over 14 hours/day, every day)'))
                    : Promise.resolve(),
              },
            ]}
            className="mb-4"
          >
            <InputNumber size="large" addonAfter="Hours" min={0} controls={false} className="w-48" />
          </Form.Item>

          <Form.Item
            name="businessCount"
            label="Number of businesses"
            rules={[{ required: true, message: 'Please select the number of businesses' }]}
            className="mt-8 mb-4"
          >
            <Select
              options={[
                {
                  title: '1 business',
                  id: 1,
                },
                {
                  title: '2 businesses',
                  id: 2,
                },
                {
                  title: '3 businesses',
                  id: 3,
                },
                {
                  title: '4 businesses',
                  id: 4,
                },
                {
                  title: '5 or more businesses',
                  id: 5,
                },
              ]}
              getOptionValue={(v) => v.id}
              getOptionLabel={(opt) => opt.title}
              placeholder="Select..."
              ariaLabel="Number of businesses"
            />
          </Form.Item>

          {typeof businessCountFormValue?.id === 'number' && businessCountFormValue?.id !== 1 && (
            <Alert
              type="info"
              showIcon
              message="Multiple Businesses Documentation"
              description="You'll need to submit separate documentation for each business during the verification step"
              className="mt-4"
            />
          )}
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.SPOUSE_SELF_EMPLOYMENT,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
          }}
        />
      </Form>
    </Spin>
  );
}

function MinimumWageInfo() {
  return (
    <RevealingLink
      text="View minimum wage info"
      expandedText="Hide minimum wage info"
      reveals={
        <Alert
          type="info"
          showIcon
          message="Minimum Wage Requirement"
          description={
            <>
              <p>
                Self-employment eligibility requires that your annual income divided by total annual hours worked equals
                at least your state's minimum wage.
              </p>

              <p className="mt-4">
                For example: If you work 20 hours per week (1,040 hours per year) in a state with a $15/hour minimum
                wage, your annual income must be at least $15,600.
              </p>

              <ExternalLink
                href="https://www.dol.gov/agencies/whd/minimum-wage/state"
                text="Check current minimum wage in your state"
                className="block mt-4"
              >
                Check current minimum wage in your state
              </ExternalLink>
            </>
          }
        />
      }
      className="mt-3"
    />
  );
}

export default FamilyAppSpouseSelfEmployment;
